import { isSmallScreen, prefersReducedMotion } from '@/effects/motion';
import * as THREE from 'three';

type Hero3DHandle = {
  destroy: () => void;
};

// ────────────────────────────────────────────────────────────
//  GLSL Shaders – Aurora sphere
// ────────────────────────────────────────────────────────────
const AURORA_VERT = /* glsl */ `
  uniform float uTime;
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vNormal   = normalize(normalMatrix * normal);
    vPosition = position;

    // Organic vertex displacement
    vec3 pos = position;
    float d  = sin(pos.x * 2.8 + uTime * 0.9)
             * cos(pos.y * 2.8 + uTime * 0.65)
             * sin(pos.z * 2.8 + uTime * 0.45) * 0.08;
    pos += normal * d;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const AURORA_FRAG = /* glsl */ `
  uniform float uTime;
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    // Rim / Fresnel glow
    float fresnel = pow(1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0))), 2.5);

    // Animated aurora bands
    vec3 purple = vec3(0.545, 0.361, 0.965);
    vec3 cyan   = vec3(0.133, 0.827, 0.933);
    vec3 blue   = vec3(0.231, 0.510, 0.965);
    vec3 pink   = vec3(0.937, 0.392, 0.706);

    float t1 = sin(vPosition.y * 3.2 + uTime * 0.65) * 0.5 + 0.5;
    float t2 = sin(vPosition.x * 2.1 - uTime * 0.40) * 0.5 + 0.5;
    float t3 = sin(vPosition.z * 4.0 + uTime * 0.30) * 0.5 + 0.5;

    vec3 col = mix(purple, cyan,  t1);
         col = mix(col,   blue,   t2 * 0.6);
         col = mix(col,   pink,   t3 * 0.22);

    float alpha = (fresnel * 0.82 + 0.12);
    gl_FragColor = vec4(col, alpha);
  }
`;

export const initHero3D = async (): Promise<Hero3DHandle | null> => {
  const canvas = document.getElementById('hero-3d-canvas') as HTMLCanvasElement | null;
  if (!canvas) return null;

  if (prefersReducedMotion()) return null;

  const mobileMode = isSmallScreen();

  // ── Scene / Camera / Renderer ──
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x050510, 0.022);

  const camera = new THREE.PerspectiveCamera(65, 1, 0.1, 100);
  camera.position.z = mobileMode ? 6.5 : 5.5;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: !mobileMode,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // Solid background matching the fog colour – eliminates edge flicker caused
  // by AdditiveBlending particles writing near-zero alpha against a transparent canvas.
  renderer.setClearColor(0x050510, 1);

  const updateSize = (): void => {
    const parent = canvas.parentElement;
    const width = parent?.clientWidth ?? window.innerWidth;
    const height = parent?.clientHeight ?? Math.max(420, window.innerHeight * 0.65);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };
  updateSize();
  window.addEventListener('resize', updateSize);

  // ── Mouse state ──
  let rawMouseX = 0;
  let rawMouseY = 0;
  let smoothMouseX = 0;
  let smoothMouseY = 0;

  const onMouseMove = (e: MouseEvent): void => {
    rawMouseX = (e.clientX / window.innerWidth - 0.5) * 2;
    rawMouseY = -(e.clientY / window.innerHeight - 0.5) * 2;
  };
  if (!mobileMode) window.addEventListener('mousemove', onMouseMove);

  // ── LAYER 1 – Deep star field ──
  const starCount = mobileMode ? 700 : 1400;
  const starPositions = new Float32Array(starCount * 3);
  const starColors = new Float32Array(starCount * 3);

  const cWhite = new THREE.Color(0xffffff);
  const cViolet = new THREE.Color(0x8b5cf6);
  const cCyan = new THREE.Color(0x22d3ee);

  for (let i = 0; i < starCount; i++) {
    const i3 = i * 3;
    const r = 14 + Math.random() * 10;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    starPositions[i3] = r * Math.sin(phi) * Math.cos(theta);
    starPositions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    starPositions[i3 + 2] = r * Math.cos(phi);

    const rand = Math.random();
    const sc =
      rand < 0.55
        ? cWhite.clone().lerp(cViolet, Math.random() * 0.35)
        : rand < 0.8
          ? cWhite.clone().lerp(cCyan, Math.random() * 0.3)
          : cWhite.clone();
    starColors[i3] = sc.r;
    starColors[i3 + 1] = sc.g;
    starColors[i3 + 2] = sc.b;
  }

  const starGeo = new THREE.BufferGeometry();
  starGeo.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
  starGeo.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
  const starMat = new THREE.PointsMaterial({
    size: mobileMode ? 0.07 : 0.055,
    vertexColors: true,
    transparent: true,
    opacity: 0.88,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
    depthWrite: false,
  });
  const starField = new THREE.Points(starGeo, starMat);
  scene.add(starField);

  // ── LAYER 2 – Constellation network ──
  const netGroup = new THREE.Group();
  scene.add(netGroup);

  const netCount = mobileMode ? 110 : 200;
  const NET_RADIUS = mobileMode ? 3.0 : 4.2;
  const CONN_DIST_SQ = (mobileMode ? 1.35 : 1.75) ** 2;
  const MAX_LINES = 450;

  const netPos = new Float32Array(netCount * 3);
  const netVel = new Float32Array(netCount * 3);

  for (let i = 0; i < netCount; i++) {
    const i3 = i * 3;
    const r = Math.random() * NET_RADIUS * 0.85;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 2 - 1);
    netPos[i3] = r * Math.sin(phi) * Math.cos(theta);
    netPos[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    netPos[i3 + 2] = r * Math.cos(phi);
    netVel[i3] = (Math.random() - 0.5) * 0.0022;
    netVel[i3 + 1] = (Math.random() - 0.5) * 0.0022;
    netVel[i3 + 2] = (Math.random() - 0.5) * 0.0014;
  }

  const netGeo = new THREE.BufferGeometry();
  const netPosAttr = new THREE.BufferAttribute(netPos, 3);
  netGeo.setAttribute('position', netPosAttr);

  const dotMat = new THREE.PointsMaterial({
    size: 0.045,
    color: 0xa78bfa,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
    depthWrite: false,
  });
  netGroup.add(new THREE.Points(netGeo, dotMat));

  const linePos = new Float32Array(MAX_LINES * 6);
  const lineGeo = new THREE.BufferGeometry();
  const linePosAttr = new THREE.BufferAttribute(linePos, 3);
  lineGeo.setAttribute('position', linePosAttr);
  const lineMat = new THREE.LineBasicMaterial({
    color: 0x7c3aed,
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  netGroup.add(new THREE.LineSegments(lineGeo, lineMat));

  const updateConstellation = (): void => {
    let idx = 0;
    outer: for (let i = 0; i < netCount; i++) {
      for (let j = i + 1; j < netCount; j++) {
        const i3 = i * 3,
          j3 = j * 3;
        const dx = netPos[i3]! - netPos[j3]!;
        const dy = netPos[i3 + 1]! - netPos[j3 + 1]!;
        const dz = netPos[i3 + 2]! - netPos[j3 + 2]!;
        if (dx * dx + dy * dy + dz * dz < CONN_DIST_SQ) {
          const l = idx * 6;
          linePos[l] = netPos[i3]!;
          linePos[l + 1] = netPos[i3 + 1]!;
          linePos[l + 2] = netPos[i3 + 2]!;
          linePos[l + 3] = netPos[j3]!;
          linePos[l + 4] = netPos[j3 + 1]!;
          linePos[l + 5] = netPos[j3 + 2]!;
          idx++;
          if (idx >= MAX_LINES) break outer;
        }
      }
    }
    for (let i = idx; i < MAX_LINES; i++) {
      const l = i * 6;
      for (let k = 0; k < 6; k++) linePos[l + k] = 0;
    }
    linePosAttr.needsUpdate = true;
    lineGeo.setDrawRange(0, idx * 2);
  };
  updateConstellation();

  // ── LAYER 3 – Aurora sphere ──
  const sphereGroup = new THREE.Group();
  scene.add(sphereGroup);

  const SR = mobileMode ? 0.88 : 1.28;

  const auroraMat = new THREE.ShaderMaterial({
    vertexShader: AURORA_VERT,
    fragmentShader: AURORA_FRAG,
    uniforms: { uTime: { value: 0 } },
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const auroraGeo = new THREE.SphereGeometry(SR, mobileMode ? 32 : 64, mobileMode ? 32 : 64);
  sphereGroup.add(new THREE.Mesh(auroraGeo, auroraMat));

  // Bloom halo (back-face, slightly larger)
  const bloomGeo = new THREE.SphereGeometry(SR * 1.18, 16, 16);
  const bloomMat = new THREE.MeshBasicMaterial({
    color: 0x7c3aed,
    transparent: true,
    opacity: 0.07,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.BackSide,
  });
  sphereGroup.add(new THREE.Mesh(bloomGeo, bloomMat));

  // Wide outer halo
  const haloGeo = new THREE.SphereGeometry(SR * 1.48, 10, 10);
  const haloMat = new THREE.MeshBasicMaterial({
    color: 0x22d3ee,
    transparent: true,
    opacity: 0.032,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.BackSide,
  });
  sphereGroup.add(new THREE.Mesh(haloGeo, haloMat));

  // Geometric wireframe overlay
  const wireGeo = new THREE.IcosahedronGeometry(SR * 1.04, 2);
  const wireMat = new THREE.MeshBasicMaterial({
    color: 0x8b5cf6,
    wireframe: true,
    transparent: true,
    opacity: 0.07,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const wireMesh = new THREE.Mesh(wireGeo, wireMat);
  sphereGroup.add(wireMesh);

  // ── LAYER 4 – Orbital rings ──
  const ring1Group = new THREE.Group();
  ring1Group.rotation.x = Math.PI / 3;
  ring1Group.rotation.y = Math.PI / 6;
  scene.add(ring1Group);

  const ring2Group = new THREE.Group();
  ring2Group.rotation.x = Math.PI / 4;
  ring2Group.rotation.z = Math.PI / 3;
  scene.add(ring2Group);

  const ring1Geo = new THREE.TorusGeometry(SR * 1.72, 0.007, 8, 128);
  const ring1Mat = new THREE.MeshBasicMaterial({
    color: 0x8b5cf6,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  ring1Group.add(new THREE.Mesh(ring1Geo, ring1Mat));

  const ring2Geo = new THREE.TorusGeometry(SR * 2.15, 0.005, 8, 128);
  const ring2Mat = new THREE.MeshBasicMaterial({
    color: 0x22d3ee,
    transparent: true,
    opacity: 0.38,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  ring2Group.add(new THREE.Mesh(ring2Geo, ring2Mat));

  // Glowing satellite dots on ring 1
  const satCount = mobileMode ? 3 : 6;
  const satGeo = new THREE.SphereGeometry(0.045, 8, 8);
  const satRadius = SR * 1.72;
  const satellites: THREE.Mesh[] = [];
  for (let i = 0; i < satCount; i++) {
    const satMat = new THREE.MeshBasicMaterial({
      color: 0xa78bfa,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
    });
    const sat = new THREE.Mesh(satGeo, satMat);
    const angle = (i / satCount) * Math.PI * 2;
    sat.position.set(Math.cos(angle) * satRadius, 0, Math.sin(angle) * satRadius);
    ring1Group.add(sat);
    satellites.push(sat);
  }

  // ── Lights ──
  scene.add(new THREE.AmbientLight(0xffffff, 0.15));

  const pl1 = new THREE.PointLight(0x8b5cf6, 3.5, 14);
  pl1.position.set(3, 2, 3);
  scene.add(pl1);

  const pl2 = new THREE.PointLight(0x22d3ee, 3.0, 14);
  pl2.position.set(-3, -2, 2);
  scene.add(pl2);

  const pl3 = new THREE.PointLight(0xec4899, 2.0, 10);
  pl3.position.set(0, 3, -2);
  scene.add(pl3);

  // ── Animation loop ──
  const clock = new THREE.Clock();
  let frameCount = 0;
  let animFrameId: number;

  const tick = (): void => {
    const t = clock.getElapsedTime();
    frameCount++;

    smoothMouseX += (rawMouseX - smoothMouseX) * 0.04;
    smoothMouseY += (rawMouseY - smoothMouseY) * 0.04;

    // Star field slow drift
    starField.rotation.y = t * 0.007;
    starField.rotation.x = t * 0.003;

    // Constellation particle physics
    for (let i = 0; i < netCount; i++) {
      const i3 = i * 3;
      netPos[i3] = netPos[i3]! + netVel[i3]!;
      netPos[i3 + 1] = netPos[i3 + 1]! + netVel[i3 + 1]!;
      netPos[i3 + 2] = netPos[i3 + 2]! + netVel[i3 + 2]!;

      const dx = netPos[i3]!,
        dy = netPos[i3 + 1]!,
        dz = netPos[i3 + 2]!;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > NET_RADIUS) {
        const nx = dx / dist,
          ny = dy / dist,
          nz = dz / dist;
        const vDotN = netVel[i3]! * nx + netVel[i3 + 1]! * ny + netVel[i3 + 2]! * nz;
        if (vDotN > 0) {
          netVel[i3] = netVel[i3]! - 2 * vDotN * nx;
          netVel[i3 + 1] = netVel[i3 + 1]! - 2 * vDotN * ny;
          netVel[i3 + 2] = netVel[i3 + 2]! - 2 * vDotN * nz;
        }
        const pull = NET_RADIUS * 0.97;
        netPos[i3] = nx * pull;
        netPos[i3 + 1] = ny * pull;
        netPos[i3 + 2] = nz * pull;
      }
    }
    netPosAttr.needsUpdate = true;
    netGroup.rotation.y = t * 0.04;
    if (frameCount % 3 === 0) updateConstellation();

    // Aurora sphere
    auroraMat.uniforms['uTime']!.value = t;
    const breathe = 1 + Math.sin(t * 1.15) * 0.028;
    sphereGroup.scale.setScalar(breathe);
    sphereGroup.rotation.y = t * 0.1;
    sphereGroup.rotation.x = t * 0.06;
    sphereGroup.position.x = smoothMouseX * 0.35;
    sphereGroup.position.y = smoothMouseY * 0.25;
    wireMesh.rotation.z = -t * 0.08;
    wireMesh.rotation.x = t * 0.05;

    // Orbital rings
    ring1Group.rotation.z = t * 0.45;
    ring1Group.position.x = smoothMouseX * 0.18;
    ring1Group.position.y = smoothMouseY * 0.14;
    ring2Group.rotation.z = -t * 0.28;
    ring2Group.position.x = smoothMouseX * 0.1;
    ring2Group.position.y = smoothMouseY * 0.08;

    // Animated lights orbit
    pl1.position.x = Math.cos(t * 0.55) * 4.5;
    pl1.position.z = Math.sin(t * 0.55) * 4.5;
    pl2.position.x = Math.cos(t * 0.4 + 2.0) * 4.5;
    pl2.position.z = Math.sin(t * 0.4 + 2.0) * 4.5;
    pl3.position.y = Math.sin(t * 0.48) * 3.5;

    // Camera gentle float + mouse parallax
    camera.position.x = Math.sin(t * 0.07) * 0.4 + smoothMouseX * 0.55;
    camera.position.y = Math.cos(t * 0.05) * 0.25 + smoothMouseY * 0.4;
    camera.lookAt(0, 0, 0);

    renderer.render(scene, camera);
    animFrameId = requestAnimationFrame(tick);
  };

  tick();

  return {
    destroy: (): void => {
      window.removeEventListener('resize', updateSize);
      if (!mobileMode) window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(animFrameId);

      [
        starGeo,
        netGeo,
        lineGeo,
        auroraGeo,
        bloomGeo,
        haloGeo,
        wireGeo,
        satGeo,
        ring1Geo,
        ring2Geo,
      ].forEach(g => g.dispose());

      [starMat, dotMat, lineMat, auroraMat, bloomMat, haloMat, wireMat, ring1Mat, ring2Mat].forEach(
        m => m.dispose()
      );

      satellites.forEach(s => (s.material as THREE.Material).dispose());
      renderer.dispose();
    },
  };
};
